import React, { useEffect } from "react";
import {
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { USER_PROFILE_DATA, GENDER2 } from "../../constants";
import Input from "../../../../component-lib/Atoms/Input/Input";
import Button from "../../../../component-lib/Molecules/button/Button";
import { TEXT_CONSTANTS } from "../../../../utils/text-constants/textConstants";
import SelectDropdown from "../../../../component-lib/Atoms/Select/Select";
import { VALIDATION_CONSTANTS } from "../../../../utils/validation-constants/validationConstants";
import BootstrapDialog from "../../../../utils/bootstrap-dialog-style/BootstrapDialog";
import { useDispatch, useSelector } from "react-redux";
import { getDepartmentList, getRolesList } from "../../FacilityManagementSaga";
import { componentKey } from "../../FacilityManagementSlice";
import ContactInput from "../../../../component-lib/Atoms/Input/ContactInput";
import { normalizeContactNumber } from "../../../../utils/phoneNumberFormatter";
import MultipleSelectWithEvents from "../../../../component-lib/Atoms/Select/MultiSelect";
import { useParams } from "react-router-dom";

interface ProviderProfileDialogProps {
  isOpen: boolean;
  handleData: (data: any) => void;
  handleClose: () => void;
  departmentName?: any;
  defaultDataForUser?: any;
  isEdit?: boolean;
  dialogHeading?: string;
  selectedTabForUser?: string;
}

export default function ProviderProfileDialog({
  isOpen = false,
  departmentName,
  defaultDataForUser,
  handleClose,
  handleData,
  isEdit = false,
  dialogHeading,
  selectedTabForUser,
}: ProviderProfileDialogProps) {
  const { rolesList, departmentList } = useSelector(
    (state: any) => state[componentKey]
  );
  const { facilityId } = useParams();
  const roles = rolesList?.map((data: any) => ({
    value: data?.id,
    label: data?.name,
  }));
  //TO FIND THE LABEL AND AS ROLE AND TO GET THE ID AS PER NAME OF ROLE
  const findRole =
    roles?.find((role: any) => role.label === defaultDataForUser?.role)
      ?.value || "";

  // Only mount the form once roles AND departments have loaded. Both feed
  // `initialValues` (findRole) / the department options, and they load async;
  // if the form mounts first, Formik's enableReinitialize + the MultiSelect's
  // options-reset fire mid-edit and wipe the user's Department selection before
  // save (that's the "says updated but doesn't stick" bug). Gating renders the
  // form with stable initial data so nothing resets after mount.
  const isFormDataReady =
    Array.isArray(rolesList) &&
    rolesList.length > 0 &&
    Array.isArray(departmentList?.department);

  const dispatch = useDispatch();
  const validationSchema = Yup.object().shape({
    [USER_PROFILE_DATA.BSA_ID]: Yup.number()
      // .matches(VALIDATION_CONSTANTS.NUMERIC, "BSA ID must contain only numbers")
      .required("BSA ID is required"),
    [USER_PROFILE_DATA?.FIRST_NAME]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.CHARACTER,
        "First name can only contain characters"
      )
      .required("First name is required"),
    [USER_PROFILE_DATA?.LAST_NAME]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.CHARACTER,
        "Last name can only contain characters"
      )
      .required("Last name is required"),
    [USER_PROFILE_DATA?.ROLE]: Yup.string().required("Role is required"),
    [USER_PROFILE_DATA?.GENDER]: Yup.string().required("Gender is required"),
    [USER_PROFILE_DATA?.EMAIL]: Yup.string()
      // .email("Enter a valid email address")
      .matches(VALIDATION_CONSTANTS.EMAIL, "Enter a valid email address")
      .required("Email is required"),
    [USER_PROFILE_DATA?.WORK_LOCATION]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.NAME,
        "Work location can only contain alphanumeric characters"
      )
      .required("Work location is required"),

    [USER_PROFILE_DATA?.DEPARTMENT]: Yup.array()
      .min(1, "At least one name must be selected")
      .required("This field is required"),

    [USER_PROFILE_DATA?.CONTACT_NUMBER]: Yup.string()
      .matches(VALIDATION_CONSTANTS.CONTACT_NUMBER, "Invalid contact number")
      .required("Contact number is required"),
  });

  useEffect(() => {
    const payload = {
      pageNumber: 1,
      limit: 200,
      type: selectedTabForUser === "Staff" ? "staff" : "provider",
    };
    dispatch(getRolesList(payload));
    getDepartments();
  }, []);

  const options = departmentList?.department?.map((data: any) => ({
    label: data?.name,
    value: data?.id,
  }));

  // GET DEPARTMENTS
  const getDepartments = () => {
    const payload = {
      facilityId: facilityId,
      pageNumber: 1,
      // Fetch all departments for the assignment picker — the backend defaults
      // to a page size of 10, which would hide departments 11+ and make them
      // impossible to assign to a user. Mirrors the roles fetch (limit: 200).
      limit: 200,
    };
    dispatch(getDepartmentList(payload));
  };

  return (
    <BootstrapDialog
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="customized-dialog-title"
      open={isOpen}
    >
      {!isFormDataReady ? (
        <div className="flex justify-center items-center py-16">
          <CircularProgress />
        </div>
      ) : (
      <Formik
        initialValues={{
          [USER_PROFILE_DATA?.BSA_ID]: `${defaultDataForUser?.bsaId || ""}`,
          [USER_PROFILE_DATA?.FIRST_NAME]: `${defaultDataForUser?.firstName || ""}`,
          [USER_PROFILE_DATA?.LAST_NAME]: `${defaultDataForUser?.lastName || ""}`,
          [USER_PROFILE_DATA?.EMAIL]: `${defaultDataForUser?.email || ""}`,
          [USER_PROFILE_DATA.ROLE]: `${findRole || ""}`,
          [USER_PROFILE_DATA?.GENDER]: `${defaultDataForUser?.gender || ""}`,
          [USER_PROFILE_DATA?.WORK_LOCATION]: `${departmentName || ""}`,
          [USER_PROFILE_DATA?.DEPARTMENT]:
            defaultDataForUser?.departments?.map(
              (data: any) => data?.department?.id
            ) || [],
          [USER_PROFILE_DATA?.CONTACT_NUMBER]: normalizeContactNumber(
            defaultDataForUser?.contactNumber
          ),
        }}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          try {
            handleData(values);
          } catch (error) {
            console.log(error);
          } finally {
            setSubmitting(false); // Reset submitting state
          }
        }}
      >
        {({
          errors,
          values,
          touched,
          setFieldValue,
          handleBlur,
          handleSubmit,
        }: any) => {
          return (
            <Form onSubmit={handleSubmit}>
              <DialogTitle
                sx={{
                  m: 0,
                  p: 2,
                  fontWeight: 600,
                  fontSize: "18px",
                  fontFamily: "inter",
                }}
                id="customized-dialog-title"
              >
                {dialogHeading}
              </DialogTitle>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: theme.palette.grey[500],
                })}
              >
                <CloseIcon />
              </IconButton>
              <hr />
              {/* MAIN CONTENT */}
              <DialogContent>
                <div
                  style={{
                    // height: deviceType === "mobile" ? "40vh" : "fit-content",
                    overflow: "auto",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                  }}
                >
                  <div className="pt-4">
                    <Input
                      disabled={defaultDataForUser?.bsaId ? true : false}
                      type="number"
                      textFieldLabel="BSA ID"
                      // isStarPresent={true}
                      name={USER_PROFILE_DATA.BSA_ID}
                      value={values[USER_PROFILE_DATA.BSA_ID]}
                      onChangeCb={(e) => {
                        setFieldValue(USER_PROFILE_DATA.BSA_ID, e.target.value);
                      }}
                      onBlur={handleBlur}
                      size="small"
                      error={
                        touched[USER_PROFILE_DATA.BSA_ID] &&
                        Boolean(errors[USER_PROFILE_DATA.BSA_ID])
                      }
                      helperText={
                        touched[USER_PROFILE_DATA.BSA_ID]
                          ? errors[USER_PROFILE_DATA.BSA_ID]
                          : undefined
                      }
                    />
                  </div>

                  <div className="pt-4">
                    <Input
                      textFieldLabel="First Name"
                      // placeholder=""
                      maxLength={20}
                      size="small"
                      name={USER_PROFILE_DATA.FIRST_NAME}
                      value={values[USER_PROFILE_DATA.FIRST_NAME]}
                      onChangeCb={(e) => {
                        setFieldValue(
                          USER_PROFILE_DATA.FIRST_NAME,
                          e.target.value
                        );
                      }}
                      onBlur={handleBlur}
                      error={
                        touched[USER_PROFILE_DATA.FIRST_NAME] &&
                        Boolean(errors[USER_PROFILE_DATA.FIRST_NAME])
                      }
                      helperText={
                        touched[USER_PROFILE_DATA.FIRST_NAME]
                          ? errors[USER_PROFILE_DATA.FIRST_NAME]
                          : undefined
                      }
                    />
                  </div>

                  <Input
                    textFieldLabel="Last Name"
                    // placeholder=""
                    maxLength={20}
                    size="small"
                    name={USER_PROFILE_DATA.LAST_NAME}
                    value={values[USER_PROFILE_DATA.LAST_NAME]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        USER_PROFILE_DATA.LAST_NAME,
                        e.target.value
                      );
                    }}
                    onBlur={handleBlur}
                    error={
                      touched[USER_PROFILE_DATA.LAST_NAME] &&
                      Boolean(errors[USER_PROFILE_DATA.LAST_NAME])
                    }
                    helperText={
                      touched[USER_PROFILE_DATA.LAST_NAME]
                        ? errors[USER_PROFILE_DATA.LAST_NAME]
                        : undefined
                    }
                  />
                  <Input
                    textFieldLabel="Email"
                    // placeholder=""
                    size="small"
                    name={USER_PROFILE_DATA.EMAIL}
                    value={values[USER_PROFILE_DATA.EMAIL]}
                    onChangeCb={(e) => {
                      setFieldValue(USER_PROFILE_DATA.EMAIL, e.target.value);
                    }}
                    onBlur={handleBlur}
                    error={
                      touched[USER_PROFILE_DATA.EMAIL] &&
                      Boolean(errors[USER_PROFILE_DATA.EMAIL])
                    }
                    helperText={
                      touched[USER_PROFILE_DATA.EMAIL]
                        ? errors[USER_PROFILE_DATA.EMAIL]
                        : undefined
                    }
                  />

                  <ContactInput
                    textFieldLabel="Contact Number"
                    name={USER_PROFILE_DATA.CONTACT_NUMBER}
                    value={values[USER_PROFILE_DATA.CONTACT_NUMBER]}
                    onChangeCb={(value) => {
                      setFieldValue(USER_PROFILE_DATA.CONTACT_NUMBER, value);
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[USER_PROFILE_DATA.CONTACT_NUMBER] &&
                      Boolean(errors[USER_PROFILE_DATA.CONTACT_NUMBER])
                    }
                    helperText={
                      touched[USER_PROFILE_DATA.CONTACT_NUMBER]
                        ? errors[USER_PROFILE_DATA.CONTACT_NUMBER]
                        : undefined
                    }
                    isContactNumber={true}
                  />

                  {/* SPECIALITY DROPDOWN */}
                  <SelectDropdown
                    label="Role"
                    value={values[USER_PROFILE_DATA.ROLE]}
                    options={roles}
                    onChangeCb={(e) =>
                      setFieldValue(USER_PROFILE_DATA.ROLE, e.target.value)
                    }
                    error={
                      touched[USER_PROFILE_DATA.ROLE] &&
                      Boolean(errors[USER_PROFILE_DATA.ROLE])
                    }
                    helperText={
                      touched[USER_PROFILE_DATA.ROLE]
                        ? errors[USER_PROFILE_DATA.ROLE]
                        : undefined
                    }
                  />

                  {/* GENDER DROPDOWN */}
                  <SelectDropdown
                    label="Gender"
                    // isStarPresent={true}
                    value={values[USER_PROFILE_DATA.GENDER]}
                    options={GENDER2}
                    onChangeCb={(e) =>
                      setFieldValue(USER_PROFILE_DATA.GENDER, e.target.value)
                    }
                    error={
                      touched[USER_PROFILE_DATA.GENDER] &&
                      Boolean(errors[USER_PROFILE_DATA.GENDER])
                    }
                    helperText={
                      touched[USER_PROFILE_DATA.GENDER]
                        ? errors[USER_PROFILE_DATA.GENDER]
                        : undefined
                    }
                  />

                  <Input
                    maxLength={50}
                    textFieldLabel="Work Location"
                    disabled
                    size="small"
                    name={USER_PROFILE_DATA.WORK_LOCATION}
                    value={values[USER_PROFILE_DATA.WORK_LOCATION]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        USER_PROFILE_DATA.WORK_LOCATION,
                        e.target.value
                      );
                    }}
                    onBlur={handleBlur}
                    error={
                      touched[USER_PROFILE_DATA.WORK_LOCATION] &&
                      Boolean(errors[USER_PROFILE_DATA.WORK_LOCATION])
                    }
                    helperText={
                      touched[USER_PROFILE_DATA.WORK_LOCATION]
                        ? errors[USER_PROFILE_DATA.WORK_LOCATION]
                        : undefined
                    }
                  />
                  <MultipleSelectWithEvents
                    options={options}
                    defaultSelectedValues={
                      values[USER_PROFILE_DATA?.DEPARTMENT]
                    }
                    label="Department"
                    name={USER_PROFILE_DATA?.DEPARTMENT}
                    onItemsAdded={() => {}}
                    onItemsRemoved={() => {}}
                    onSelectionChanged={(selectedValues) => {
                      setFieldValue(
                        USER_PROFILE_DATA.DEPARTMENT,
                        selectedValues
                      );
                    }}
                    onBlur={handleBlur}
                    error={
                      touched[USER_PROFILE_DATA?.DEPARTMENT] &&
                      Boolean(errors[USER_PROFILE_DATA?.DEPARTMENT])
                    }
                    helperText={
                      touched[USER_PROFILE_DATA?.DEPARTMENT]
                        ? errors[USER_PROFILE_DATA?.DEPARTMENT]
                        : undefined
                    }
                  />
                </div>
              </DialogContent>

              {/* ACTION BUTTON */}
              <DialogActions style={{ padding: "24px" }}>
                {dialogHeading?.includes("Edit") && (
                  <Button
                    label={TEXT_CONSTANTS?.CANCEL}
                    variant="outlined"
                    onClickCb={handleClose}
                    style={{
                      width: "120px",
                      height: "44px",
                      padding: "12px 20px",
                      fontSize: "16px",
                      backgroundColor: "white",
                      border: "none",
                      color: "#393939",
                    }}
                  />
                )}
                <Button
                  type="submit"
                  label={TEXT_CONSTANTS?.SAVE}
                  variant="contained"
                  onClickCb={() => {}}
                  style={{
                    width: "200px",
                    height: "44px",
                    padding: "12px 20px",
                    fontSize: "16px",
                  }}
                ></Button>
                {/* </div> */}
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
      )}
    </BootstrapDialog>
  );
}
